import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { captureClaimToken, readClaimToken } from '../services/claimToken';
import { claimReturnPath } from '../services/authReturn';
import {
  Typography,
  TextField,
  Button,
  Box,
  Divider,
  Alert,
  CircularProgress,
  Link,
  Stack
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import BrandScreen from './brand/BrandScreen';
import BrandMark from './brand/BrandMark';
import { BRAND, BRAND_SHADOW_CTA, WEBSITE_URL } from '../brand/brand';

const Signup = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isClaiming, setIsClaiming] = useState(false);

  const { currentUser, signUp, signInWithGoogle } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = claimReturnPath(location.search) || '/dashboard';

  useEffect(() => {
    // On mount, before either signup handler runs. Both call navigate('/dashboard')
    // immediately on success, which discards the fragment -- so reading it after
    // authentication returns would be too late.
    captureClaimToken();
    setIsClaiming(Boolean(readClaimToken()));
    if (currentUser) {
      navigate(readClaimToken() ? '/dashboard' : returnTo, { replace: true });
    }
  }, [currentUser, navigate, returnTo]);

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    if (password !== confirmPassword) {
      setErrorMessage(t('auth.passwordsMismatch'));
      setIsLoading(false);
      return;
    }

    if (password.length < 6) {
      setErrorMessage(t('auth.passwordTooShort'));
      setIsLoading(false);
      return;
    }

    try {
      await signUp(email, password);
      navigate(readClaimToken() ? '/dashboard' : returnTo);
    } catch (error) {
      setErrorMessage(error.message || t('auth.signUpFailed'));
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setIsLoading(true);
    setErrorMessage('');

    try {
      await signInWithGoogle();
      navigate(readClaimToken() ? '/dashboard' : returnTo);
    } catch (error) {
      setErrorMessage(error.message || t('auth.googleSignUpFailed'));
      setIsLoading(false);
    }
  };

  return (
    <BrandScreen maxWidth="sm">
      <Stack alignItems="center" spacing={1} mb={3}>
        <BrandMark
          size={64}
          title={t('auth.brandTitle')}
          subtitle={t('auth.signupSubtitle')}
        />
      </Stack>

      {isClaiming && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('auth.claimApprovedSignup')}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {/* Google is the primary, first-choice sign-in — mirrors the mobile login screen. */}
      <Button
        fullWidth
        variant="contained"
        size="large"
        startIcon={<GoogleIcon />}
        onClick={handleGoogleSignUp}
        disabled={isLoading}
        sx={{
          py: 1.5,
          borderRadius: '999px',
          backgroundColor: '#fff',
          color: BRAND.ink700,
          border: `1px solid ${BRAND.line}`,
          boxShadow: BRAND_SHADOW_CTA,
          '&:hover': {
            backgroundColor: BRAND.purple50,
            borderColor: BRAND.purple400,
            boxShadow: BRAND_SHADOW_CTA,
          },
          fontSize: '1rem',
          fontWeight: 700,
        }}
      >
        {isLoading ? <CircularProgress size={24} color="inherit" /> : t('auth.signUpWithGoogle')}
      </Button>

      <Divider sx={{ my: 3 }}>
        <Typography variant="body2" sx={{ color: BRAND.ink500 }}>
          {t('auth.or')}
        </Typography>
      </Divider>

      <Box component="form" onSubmit={handleEmailSignUp}>
        <TextField
          label={t('auth.email')}
          type="email"
          fullWidth
          margin="normal"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={isLoading}
          sx={{ mb: 2 }}
        />
        <TextField
          label={t('auth.password')}
          type="password"
          fullWidth
          margin="normal"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={isLoading}
          sx={{ mb: 2 }}
          helperText={t('auth.passwordTooShort')}
        />
        <TextField
          label={t('auth.confirmPassword')}
          type="password"
          fullWidth
          margin="normal"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          disabled={isLoading}
          sx={{ mb: 3 }}
        />
        <Button
          type="submit"
          fullWidth
          variant="outlined"
          size="large"
          disabled={isLoading}
          sx={{
            py: 1.5,
            borderRadius: '999px',
            borderColor: BRAND.line,
            color: BRAND.ink700,
            '&:hover': { borderColor: BRAND.purple400, backgroundColor: BRAND.purple50 },
            fontSize: '1rem',
            fontWeight: 600,
          }}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : t('auth.signupButton')}
        </Button>
      </Box>

      <Stack spacing={1} sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: BRAND.ink500 }}>
          {t('auth.haveAccountPrefix')}{' '}
          <Link
            component={RouterLink}
            to={`/login${location.search}`}
            sx={{
              color: BRAND.purple700,
              textDecoration: 'none',
              fontWeight: 700,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {t('auth.loginLink')}
          </Link>
        </Typography>
        <Typography variant="caption" sx={{ color: BRAND.ink500 }}>
          <Link
            href={WEBSITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {t('auth.backToSite')}
          </Link>
        </Typography>
      </Stack>
    </BrandScreen>
  );
};

export default Signup;
